import React, { useRef, useEffect, useState, useCallback } from "react";
import propTypes from "prop-types";
import useEmblaCarousel from "embla-carousel-react";
import { Image } from "@atoms";
import { useWindowSize } from "@utils";
import classNames from "classnames";

const Carousel = React.memo(
  ({
    children,
    showMultiple,
    prevButton: PrevButtonComp,
    nextButton: NextButtonComp,
    className: _className,
    buttonPosition,
    centerItems,
    showIndicators,
    thumbnails,
    gradient,
    fullWidth,
  }) => {
    // set up refs
    const carouselContainer = useRef();

    // embla handles dragging/touch, snap animation, resizing and bounds
    const [emblaRef, emblaApi] = useEmblaCarousel({
      align: "center",
      containScroll: false,
      slidesToScroll: 1,
      duration: 30,
      inViewThreshold: 0.7,
    });

    // set up states
    const { innerWidth: windowSize } = useWindowSize();
    const [currentSlide, setCurrentSlide] = useState(0);
    const [carouselWidth, setCarouselWidth] = useState(0);
    const [slidesInView, setSlidesInView] = useState([0]);
    const [canScrollPrev, setCanScrollPrev] = useState(false);
    const [canScrollNext, setCanScrollNext] = useState(false);

    // * turn children into array
    const items = React.Children.map(children, child => {
      // Checking isValidElement is the safe way and avoids a typescript
      // error too.
      if (React.isValidElement(child)) {
        return React.cloneElement(child, {
          activeState: [currentSlide, setCurrentSlide],
        });
      }
      return child;
    });

    const itemIDs = React.Children.toArray(children).map((child, i) => {
      return `child-${i + 1}`;
    });

    // keep component state in sync with embla
    const onSelect = useCallback(api => {
      setCurrentSlide(api.selectedScrollSnap());
      setCanScrollPrev(api.canScrollPrev());
      setCanScrollNext(api.canScrollNext());
      setSlidesInView(api.slidesInView());
    }, []);

    useEffect(() => {
      if (!emblaApi) {
        return undefined;
      }
      onSelect(emblaApi);
      emblaApi.on("select", onSelect);
      emblaApi.on("reInit", onSelect);
      emblaApi.on("slidesInView", onSelect);
      return () => {
        emblaApi.off("select", onSelect);
        emblaApi.off("reInit", onSelect);
        emblaApi.off("slidesInView", onSelect);
      };
    }, [emblaApi, onSelect]);

    // scroll when the slide is changed externally (indicators, thumbnails
    // or children via activeState)
    useEffect(() => {
      if (emblaApi && emblaApi.selectedScrollSnap() !== currentSlide) {
        emblaApi.scrollTo(currentSlide);
      }
    }, [currentSlide, emblaApi]);

    // measure the container for the gradient overlay widths
    useEffect(() => {
      if (carouselContainer.current) {
        setCarouselWidth(carouselContainer.current.clientWidth);
      }
    }, [windowSize]);

    return (
      <div
        className={`relative ${_className}
        ${
          gradient
            ? "px-4 sm:px-8 md:px-16 lg:px-32 -mx-4 sm:-mx-8 md:-mx-16 lg:-mx-32"
            : "-mx-2 px-2 lg:-mx-4 lg:px-4 xl:-mx-12 xl:px-12"
        }`}
      >
        {/* prev button */}
        {PrevButtonComp && (
          <div
            className="absolute top-0 bottom-0 left-0 flex items-center pl-3 z-10"
            style={{
              transform: `translateX(${
                buttonPosition.includes("-")
                  ? buttonPosition.replace("-", "")
                  : `-${buttonPosition}`
              })`,
            }}
          >
            <button
              type="button"
              className={classNames(
                "pr-px rounded-full cursor-pointer text-blue-offwhite transition-opacity duration-200",
                { "opacity-100": canScrollPrev },
                { "opacity-0 pointer-events-none": !canScrollPrev }
              )}
              onClick={() => emblaApi?.scrollPrev()}
            >
              <PrevButtonComp />
            </button>
          </div>
        )}
        {/* the actual carousel */}
        <div
          ref={carouselContainer}
          className={`card-carousel__container relative z-0 w-full max-w-[100vw] lg:max-w-none
          ${
            showMultiple
              ? "overflow-visible"
              : "overflow-x-hidden overflow-y-hidden"
          }
          `}
        >
          {gradient && (
            <span
              className={`block fade-to-${gradient}-horz absolute left-0 top-0 bottom-0 transform -translate-x-full rotate-180 pointer-events-none z-10`}
              style={{
                width: ((windowSize || 0) - carouselWidth) / 2,
              }}
            />
          )}
          {/* embla viewport — the flex container must be its direct child */}
          <div ref={emblaRef} className="carousel relative z-0 w-full">
            <div className="w-full flex">
              {items.map((slide, i) => {
                return (
                  <div
                    key={itemIDs[i]}
                    className={classNames(
                      "flex grow flex-col shrink-0 transition duration-200",
                      { "w-full": fullWidth },
                      { "w-full lg:w-auto": !fullWidth },
                      { "opacity-100": slidesInView.includes(i) },
                      { "opacity-40": !slidesInView.includes(i) }
                    )}
                  >
                    <div
                      className={`flex flex-col grow shrink-0
                      ${centerItems ? "justify-center" : "justify-start"} `}
                    >
                      {slide}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
          {gradient && (
            <div
              className={`fade-to-${gradient}-horz absolute right-0 top-0 bottom-0 transform translate-x-full pointer-events-none z-10`}
              style={{
                width: ((windowSize || 0) - carouselWidth) / 2,
              }}
            />
          )}
        </div>
        {/* next button */}
        {NextButtonComp && (
          <div
            className="absolute top-0 bottom-0 right-0 flex items-center pr-3 z-10"
            style={{ transform: `translateX(${buttonPosition})` }}
          >
            <button
              type="button"
              className={classNames(
                "pr-px rounded-full cursor-pointer text-blue-offwhite transition-opacity duration-200",
                { "opacity-100": canScrollNext },
                { "opacity-0 pointer-events-none": !canScrollNext }
              )}
              onClick={() => emblaApi?.scrollNext()}
            >
              <NextButtonComp />
            </button>
          </div>
        )}
        {showIndicators && items.length > 1 && (
          <div className="flex items-center justify-center mt-6">
            {items.map((slide, i) => {
              return (
                // eslint-disable-next-line jsx-a11y/control-has-associated-label
                <button
                  key={`indicator--${i + 1}`}
                  type="button"
                  className={classNames(
                    "w-2.5 h-2.5 md:w-3 md:h-3 rounded-full mx-2 bg-blue shrink-0",
                    { "opacity-100": currentSlide === i },
                    { "opacity-25": currentSlide !== i }
                  )}
                  aria-label={`Go to slide ${i + 1}`}
                  onClick={() => {
                    setCurrentSlide(i);
                  }}
                />
              );
            })}
          </div>
        )}
        {thumbnails?.length > 1 && items.length > 1 && (
          <div className="items-center justify-center mt-2 grid grid-cols-5 gap-2">
            {thumbnails?.map((thumb, i) => {
              return (
                // eslint-disable-next-line jsx-a11y/control-has-associated-label
                <button
                  key={`thumb--${i + 1}`}
                  type="button"
                  className={classNames(
                    "border",
                    { "border-blue": currentSlide === i },
                    { "border-transparent": currentSlide !== i }
                  )}
                  aria-label={`Go to slide ${i + 1}`}
                  onClick={() => {
                    setCurrentSlide(i);
                  }}
                >
                  <Image {...thumb} />
                </button>
              );
            })}
          </div>
        )}
      </div>
    );
  }
);

Carousel.defaultProps = {
  showMultiple: false,
  prevButton: null,
  nextButton: null,
  showIndicators: false,
  buttonPosition: "-50%",
  className: "",
  centerItems: false,
  fullWidth: true,
};

Carousel.propTypes = {
  showMultiple: propTypes.bool,
  prevButton: propTypes.func,
  centerItems: propTypes.bool,
  showIndicators: propTypes.bool,
  nextButton: propTypes.func,
  buttonPosition: propTypes.string,
  className: propTypes.string,
  children: propTypes.oneOfType([
    propTypes.arrayOf(propTypes.node),
    propTypes.node,
  ]).isRequired,
  fullWidth: propTypes.bool,
};

export default Carousel;
